import React, { useContext, useEffect, useReducer, useState } from "react";
import MwInputText from "../components/ui/MwInputText";
import MwButton from "../components/ui/MwButton";
import MwModal from "../components/ui/MwModal";
import { useTranslation } from "react-i18next";
import periodsContext from "../utils/TimeMangeHelper";
import { MwSpinnerButton } from "../components/ui";

const initialFormState = {
  startDate: "",
  endDate: "",
};

const getCurrentYearRange = () => {
  const year = new Date().getFullYear();
  return {
    startDate: `${year}-01-01`,
    endDate: `${year}-12-31`,
  };
};

const FormReducer = (state, action) => {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    case "RESET":
      return initialFormState;
    default:
      return state;
  }
};

const normalizeDateKey = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

export const TimeMangementPopup = ({
  isOpen = false,
  setIsOpen = () => {},
  onClose = () => {},
  actionData = { mode: null, periodId: null },
}) => {
  const [t] = useTranslation("global");

  const [formData, dispatchForm] = useReducer(FormReducer, initialFormState);
  const [errors, setErrors] = useState({});
  const { periods, savePeriod, saving } = useContext(periodsContext);
  const duplicateMsg = t("times.period_exists");
  const overlapMsg = t("times.period_overlap");

  const handleInputChange = (field, value) => {
    dispatchForm({ type: "SET_FIELD", field, value });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    if (
      errors.startDate === duplicateMsg ||
      errors.startDate === overlapMsg ||
      errors.endDate === overlapMsg
    ) {
      setErrors((prev) => ({ ...prev, startDate: "", endDate: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const currentYear = new Date().getFullYear();

    if (!formData.startDate?.trim()) {
      newErrors.startDate = `${t("times.startPeriod")} ${t("common.required")}`;
    }
    if (!formData.endDate?.trim()) {
      newErrors.endDate = `${t("times.endPeriod")} ${t("common.required")}`;
    }

    // Check if the period is in the current year
    if (formData.startDate && new Date(formData.startDate).getFullYear() !== currentYear) {
      newErrors.startDate = t("times.errorOnlyCurrentYear") || `${t("times.startPeriod")} ${t("common.mustBeInCurrentYear")}`;
    }
    if (formData.endDate && new Date(formData.endDate).getFullYear() !== currentYear) {
      newErrors.endDate = t("times.errorOnlyCurrentYear") || `${t("times.endPeriod")} ${t("common.mustBeInCurrentYear")}`;
    }

    if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.endDate) <= new Date(formData.startDate)
    ) {
      newErrors.endDate = t("times.errorPeriod");
    }

    if (!newErrors.startDate && !newErrors.endDate) {
      const startKey = normalizeDateKey(formData.startDate);
      const endKey = normalizeDateKey(formData.endDate);
      const duplicate = (periods || []).some((period) => {
        if (
          actionData?.mode === "EDIT" &&
          String(period.id) === String(actionData?.periodId)
        ) {
          return false;
        }
        const periodStart = normalizeDateKey(
          period.startDateInput || period.fin_start_date,
        );
        const periodEnd = normalizeDateKey(
          period.endDateInput || period.fin_end_date,
        );
        return periodStart === startKey && periodEnd === endKey;
      });

      if (duplicate) {
        newErrors.startDate = duplicateMsg;
      }
    }

    if (!newErrors.startDate && !newErrors.endDate) {
      const startKey = normalizeDateKey(formData.startDate);
      const endKey = normalizeDateKey(formData.endDate);
      const overlaps = (periods || []).some((period) => {
        if (
          actionData?.mode === "EDIT" &&
          String(period.id) === String(actionData?.periodId)
        ) {
          return false;
        }
        const periodStart = normalizeDateKey(
          period.startDateInput || period.fin_start_date,
        );
        const periodEnd = normalizeDateKey(
          period.endDateInput || period.fin_end_date,
        );
        if (!periodStart || !periodEnd) return false;
        return startKey <= periodEnd && endKey >= periodStart;
      });

      if (overlaps) {
        newErrors.startDate = overlapMsg;
        newErrors.endDate = overlapMsg;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = {
      fin_start_date: formData.startDate,
      fin_end_date: formData.endDate,
    };

    if (actionData?.mode === "EDIT" && actionData?.periodId) {
      payload.id = actionData.periodId;
    }

    try {
      await savePeriod(payload);
      handleClose();
    } catch {
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    dispatchForm({ type: "RESET" });
    setErrors({});
    if (onClose) onClose();
  };

  useEffect(() => {
    if (!isOpen) return;

    if (actionData?.mode === "EDIT") {
      const selectedPeriod = periods?.find(
        (p) => String(p.id) === String(actionData?.periodId),
      );

      if (selectedPeriod) {
        dispatchForm({
          type: "SET_FIELD",
          field: "startDate",
          value: selectedPeriod.startDateInput || "",
        });
        dispatchForm({
          type: "SET_FIELD",
          field: "endDate",
          value: selectedPeriod.endDateInput || "",
        });
      }
      return;
    }

    if (actionData?.mode === "ADD") {
      const yearRange = getCurrentYearRange();

      dispatchForm({
        type: "SET_FIELD",
        field: "startDate",
        value: yearRange.startDate,
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "endDate",
        value: yearRange.endDate,
      });
      return;
    }

    dispatchForm({ type: "RESET" });
  }, [isOpen, actionData, periods]);

  if (saving) {
    return (
      <MwModal onShow={isOpen} setOnShow={setIsOpen}>
        <div className="w-full h-[20vh] flex justify-center items-center">
          <MwSpinnerButton />
        </div>
      </MwModal>
    );
  }

  return (
    <MwModal onShow={isOpen} setOnShow={setIsOpen}>
      <div className="flex flex-col h-full w-full bg-white rounded-lg shadow-lg font-normal">
        <div className="p-4 border-b bg-slate-100">
          <h2 className="font-normal text-[#261759] text-lg">
            {actionData?.mode === "ADD"
              ? t("times.AddTime")
              : actionData?.mode === "EDIT"
                ? t("times.EditTime")
                : ""}
          </h2>
        </div>
        <div className="p-6 space-y-3 overflow-y-auto bg-slate-100">
          <div className="grid grid-cols-2 gap-4">
            <MwInputText
              label={t("times.startPeriod")}
              inputType="date"
              invalid={errors.startDate}
              value={formData.startDate}
              invalidMsg={errors.startDate}
              onChange={(e) => handleInputChange("startDate", e.target.value)}
            />
            <MwInputText
              label={t("times.endPeriod")}
              inputType="date"
              invalid={errors.endDate}
              value={formData.endDate}
              invalidMsg={errors.endDate}
              onChange={(e) => handleInputChange("endDate", e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2 bg-slate-200 p-4 rounded-b border-t border-slate-400 justify-end sticky bottom-0">
          <MwButton
            type="secondary"
            classNameCustom="!font-normal"
            onClick={handleClose}
          >
            {t("common.cancel")}
          </MwButton>
          <MwButton
            type="primary"
            classNameCustom="!font-normal"
            onClick={handleSave}
          >
            {t("common.save")}
          </MwButton>
        </div>
      </div>
    </MwModal>
  );
};
